import React from "react";

export function CapTableHero() {
  return (
    <header className="hero">
      <p className="eyebrow">Financial Tools · Cap Table Modeler</p>
      <h1>Know exactly who owns your company — before every raise.</h1>
      <p>Model your founder equity, employee option pool, SAFEs, and priced rounds. See fully-diluted ownership shift with every scenario — and understand what each round actually costs you.</p>
    </header>
  );
}

export function CapTableInstructions() {
  return (
    <details className="instructions" open data-testid="cap-instructions">
      <summary>How this cap table works — start here</summary>
      <div className="instructions-body">
        <div>
          <h4>The 60-second version</h4>
          <p>A cap table is a running ledger of who owns what percentage of your company. It changes every time you:</p>
          <ol>
            <li>Issue founder shares</li>
            <li>Create or top up an employee option pool</li>
            <li>Raise money via a SAFE or convertible note</li>
            <li>Close a priced equity round (Seed, Series A, etc.)</li>
          </ol>
          <p style={{ marginTop: 8 }}>This tool tracks each event and computes your <strong>fully-diluted</strong> ownership so you can walk into any negotiation knowing the math.</p>
        </div>
        <div>
          <h4>Suggested workflow</h4>
          <ol>
            <li><strong>Setup</strong> — enter your company name and total authorised shares (10M is standard).</li>
            <li><strong>Founders</strong> — split the founder pool in whatever ratio you agreed on.</li>
            <li><strong>Option Pool</strong> — reserve a percentage for employees (typically 10-20%). This dilutes founders <em>before</em> the priced round.</li>
            <li><strong>SAFEs</strong> — enter any SAFEs / convertibles you have already raised. They will convert at your first priced round.</li>
            <li><strong>Priced Rounds</strong> — add each round with the amount raised and the pre-money valuation. The tool will compute price-per-share, new shares issued, and dilution.</li>
            <li><strong>Summary</strong> — check the fully-diluted table at the bottom. That is what your next investor will see.</li>
          </ol>
        </div>
        <div>
          <h4>Glossary — key terms</h4>
          <p><strong>Fully-diluted ownership:</strong> your percentage after every option, warrant, and outstanding SAFE has been exercised or converted. Always negotiate on the fully-diluted number.</p>
          <p style={{ marginTop: 6 }}><strong>Pre-money valuation:</strong> what the company is worth <em>before</em> the new money comes in. Post-money = pre-money + amount raised.</p>
          <p style={{ marginTop: 6 }}><strong>Price per share:</strong> pre-money valuation ÷ pre-money fully-diluted shares. Every new share in the round is sold at this price.</p>
          <p style={{ marginTop: 6 }}><strong>Option pool top-up:</strong> most Seed and Series A investors will require a top-up so the post-round pool is 10-15%. That top-up dilutes existing holders, not the new investor.</p>
        </div>
        <div>
          <h4>SAFEs, in plain English</h4>
          <p>A SAFE (Simple Agreement for Future Equity) is a promise: I give you money now, and at your next priced round you convert my SAFE into equity at either the <strong>valuation cap</strong> or the round price minus a <strong>discount</strong> — whichever is better for me.</p>
          <p style={{ marginTop: 6 }}>If your priced-round pre-money is higher than the SAFE cap, the SAFE holder gets a discount. If it is lower, they convert at the priced-round price with the discount applied.</p>
          <p style={{ marginTop: 6 }}>This tool converts every open SAFE at your <em>first</em> priced round to keep the math simple. Multiple SAFEs are stacked in the order you enter them.</p>
        </div>
      </div>
    </details>
  );
}
